import { useRef, useState } from 'react';
import { Download, Mail, Phone, MapPin, Instagram, MessageCircle, Image as ImageIcon } from 'lucide-react';
import Seo from '../components/Seo';
import { GILDAS } from '../data/gildas';
import { waLink, TEL_DISPLAY, TEL_HREF } from '../lib/contact';
import { breadcrumb } from '../lib/seo-schemas';

const WA_PRENSA = waLink('Hola Bar Cabrero! Soy de [medio/empresa] y queria pedir info para un reportaje. Gracias!');
const WA_CATERING = waLink('Hola Bar Cabrero! Estoy organizando un evento y querria info sobre catering de gildas. Gracias!');

const PRESS_IMAGES = [
  { src: '/dishes/gilda.jpg',          label: 'Gilda artesana — bodegón' },
  { src: '/dishes/gilda-clasica.jpg',  label: 'La Clásica — primer plano' },
  { src: '/dishes/gilda-iberica.jpg',  label: 'La Ibérica' },
  { src: '/dishes/gilda-pulpo.jpg',    label: 'La de Pulpo' },
  { src: '/dishes/tortilla.jpg',       label: 'Tortilla casera' },
  { src: '/dishes/bar-interior.jpg',   label: 'Interior — la barra' },
  { src: '/dishes/bar-vermut.jpg',     label: 'Vermut del domingo' },
  { src: '/dishes/bar-exterior.jpg',   label: 'Fachada en C. Julián Ceballos' },
];

const QUOTES = [
  { text: 'Llevo viniendo al Cabrero desde los siete años. Mi padre venía con mi abuelo. Aquí no cambia nada, y eso es lo bueno.', who: 'Antonio, cliente desde 1973' },
  { text: 'La gilda más honesta del norte. Anchoa, piparra, oliva, y nada de inventos. Como tiene que ser.', who: 'Carlos R., crítico gastronómico local' },
  { text: 'Hicimos el catering de nuestra boda con ellos. 80 gildas en bandeja de madera. Los invitados todavía las recuerdan.', who: 'Lucía y Pablo, clientes de catering' },
];

const FACTS = [
  { k: '1928', v: 'Año de fundación' },
  { k: '96', v: 'Años en pie' },
  { k: '12', v: 'Gildas en carta' },
  { k: '60 km', v: 'Hasta Santoña, origen de la anchoa' },
  { k: 'DOP', v: 'Anchoa de Santoña' },
  { k: '1,40 m', v: 'Altura de la barra — la más alta de Cantabria' },
];

export default function Dossier() {
  const [printing, setPrinting] = useState(false);
  const printingRef = useRef(false);

  const handleDownload = () => {
    if (printingRef.current) return;
    printingRef.current = true;
    setPrinting(true);
    requestAnimationFrame(() => {
      try {
        window.print();
      } finally {
        setTimeout(() => {
          setPrinting(false);
          printingRef.current = false;
        }, 1000);
      }
    });
  };

  return (
    <>
      <Seo
        title="Dossier de Prensa"
        description="Dossier de Bar Cabrero — el bar más antiguo de Torrelavega (1928). Historia, carta, catering, fotos en alta y contacto para periodistas, clientes y agencias."
        path="/dossier"
        schemas={[breadcrumb([{ name: 'Dossier', path: '/dossier' }])]}
      />
      <article className={`dossier ${printing ? 'dossier-printing' : ''}`} data-testid="dossier-page">
        {/* ── Botón de descarga (oculto en impresión) ── */}
        <div className="dossier-toolbar no-print">
          <div>
            <p className="eyebrow">Dossier de prensa & comunicación</p>
            <h1 className="dossier-h1">Bar Cabrero<span className="dossier-h1-sub"> · Desde 1928</span></h1>
            <p className="dossier-lead">
              Material para periodistas, agencias, empresas y clientes de catering. Descárgalo como PDF o navega la versión web.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-g btn-dossier-download"
            onClick={handleDownload}
            data-testid="dossier-download-btn"
          >
            <Download size={16} /> Descargar dossier (PDF)
          </button>
        </div>

        {/* ── PÁGINA 1 · PORTADA ── */}
        <section className="dossier-cover">
          <div className="dc-meta">
            <p className="dc-eyebrow">DOSSIER · {new Date().getFullYear()}</p>
            <p className="dc-loc">Torrelavega · Cantabria · España</p>
          </div>
          <div className="dossier-headline">
            <h2 className="dc-brand">Bar <em>Cabrero</em></h2>
            <p className="dc-tag">El bar más antiguo de Torrelavega. Gildas artesanas, tortilla casera y la barra más alta de Cantabria.</p>
            <p className="dc-since">Est. 1928 · Marcos Argumosa</p>
          </div>
          <div className="dc-foot">
            <p>barcabrero.com · {TEL_DISPLAY} · @cabrerobar</p>
          </div>
        </section>

        {/* ── La casa en 30 segundos ── */}
        <section className="dossier-sec page-break">
          <p className="ds-eyebrow">La casa en 30 segundos</p>
          <h3 className="ds-h">Casi un siglo haciendo gildas a mano</h3>
          <div className="ds-pitch">
            <p>
              El <strong>Bar Cabrero</strong> abrió en 1928 en la calle Julián Ceballos, en pleno centro de Torrelavega.
              Marcos Argumosa lo lleva con la misma filosofía de siempre: <strong>producto del norte, manos cántabras, nada de prisas</strong>.
            </p>
            <p>
              Las gildas — anchoa de Santoña DOP, piparra y oliva manzanilla — se ensartan a mano cada mañana sobre la barra,
              que con sus 1,40 m de altura es la más alta de Cantabria. Tortilla casera, canapés y, los domingos, el vermut de grifo
              que es el aperitivo de toda la ciudad.
            </p>
            <p>
              Sin reservas, sin reinvenciones, sin pretensiones. Solo el ritual de la barra del norte como llevan haciéndolo aquí
              cuatro generaciones.
            </p>
          </div>
        </section>

        {/* ── Hitos y cifras ── */}
        <section className="dossier-sec">
          <p className="ds-eyebrow">Hitos y cifras</p>
          <h3 className="ds-h">Bar Cabrero en datos</h3>
          <ul className="ds-facts" data-testid="dossier-facts">
            {FACTS.map((f) => (
              <li key={f.k}>
                <span className="ds-fact-k">{f.k}</span>
                <span className="ds-fact-v">{f.v}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Las 12 Gildas ── */}
        <section className="dossier-sec page-break">
          <p className="ds-eyebrow">La carta · Las 12 gildas</p>
          <h3 className="ds-h">Doce variedades, todas hechas a mano</h3>
          <div className="ds-gildas">
            {GILDAS.map((g) => {
              const thumb = g.img.replace('/dishes/', '/dishes/thumbs/').replace(/\.jpg$/, '.webp');
              return (
                <div key={g.num} className="ds-gilda">
                  <img src={thumb} alt={g.name} loading="lazy" />
                  <div className="ds-gilda-body">
                    <p className="ds-gilda-num">{g.num}</p>
                    <p className="ds-gilda-name">{g.name}</p>
                    <p className="ds-gilda-desc">{g.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Resto de la carta ── */}
        <section className="dossier-sec">
          <p className="ds-eyebrow">Otros platos</p>
          <h3 className="ds-h">Lo que también se come y se bebe</h3>
          <div className="ds-menu">
            <div>
              <h4>Tortilla casera</h4>
              <p>Tortilla de patata jugosa, hecha en cazuela pequeña, con cebolla pochada lenta. Pincho o ración.</p>
            </div>
            <div>
              <h4>Canapés caseros</h4>
              <p>Diez bocados clásicos: anchoa, ahumados, queso, encurtidos. Producto del norte.</p>
            </div>
            <div>
              <h4>Vermut del domingo</h4>
              <p>De 11:30 a 16:00, vermut de grifo, gildas y canapés. El aperitivo de Torrelavega desde siempre.</p>
            </div>
            <div>
              <h4>Vinos y cervezas</h4>
              <p>Selección corta: blanco de la Nava, tintos cántabros, sidra natural y caña bien tirada.</p>
            </div>
          </div>
        </section>

        {/* ── Catering y eventos ── */}
        <section className="dossier-sec page-break">
          <p className="ds-eyebrow">Para clientes y agencias</p>
          <h3 className="ds-h">Catering y eventos</h3>
          <div className="ds-catering">
            <p>
              Bandejas de gildas artesanas para <strong>bodas, eventos corporativos, aperitivos de oficina y celebraciones familiares</strong>.
              Producto fresco preparado el mismo día, transportado en bandeja sellada.
            </p>
            <ul className="ds-catering-list">
              <li><strong>Pedido mínimo:</strong> 12 gildas</li>
              <li><strong>Antelación:</strong> 2 días para pedidos pequeños · 7 días para eventos &gt; 50 pax</li>
              <li><strong>Formato:</strong> bandejas individuales o de madera para evento</li>
              <li><strong>Reparto:</strong> recogida en local o entrega concertada en Torrelavega y alrededores</li>
              <li><strong>Presupuesto:</strong> a medida, sin compromiso</li>
            </ul>
            <p className="ds-catering-cta no-print">
              <a href={WA_CATERING} target="_blank" rel="noopener noreferrer" className="btn btn-g" data-testid="dossier-catering-wa">
                <MessageCircle size={14} /> Pedir presupuesto por WhatsApp
              </a>
            </p>
          </div>
        </section>

        {/* ── Visítanos ── */}
        <section className="dossier-sec">
          <p className="ds-eyebrow">Visítanos</p>
          <h3 className="ds-h">Cómo llegar y horarios</h3>
          <div className="ds-visit">
            <div>
              <h4>Dirección</h4>
              <p>C. Julián Ceballos 19<br />39300 Torrelavega · Cantabria</p>
              <p className="ds-mini">A 5 min andando de la estación de FEVE y de la Plaza Mayor.</p>
            </div>
            <div>
              <h4>Horario</h4>
              <p>
                Lunes, miércoles y jueves: 11:00–16:00 · 19:00–23:00<br />
                Viernes y sábado: 11:00–16:00 · 19:00–00:00<br />
                Domingo (vermut): 11:30–16:00<br />
                <em>Martes cerrado</em>
              </p>
            </div>
          </div>
        </section>

        {/* ── Citas ── */}
        <section className="dossier-sec">
          <p className="ds-eyebrow">Lo que dicen de nosotros</p>
          <h3 className="ds-h">Citas seleccionadas</h3>
          <div className="ds-quotes">
            {QUOTES.map((q, i) => (
              <blockquote key={i} className="ds-quote">
                <p>&ldquo;{q.text}&rdquo;</p>
                <footer>— {q.who}</footer>
              </blockquote>
            ))}
          </div>
        </section>

        {/* ── Galería descargable ── */}
        <section className="dossier-sec page-break no-print-images" data-testid="dossier-press-images">
          <p className="ds-eyebrow">Material gráfico</p>
          <h3 className="ds-h">Fotos en alta resolución</h3>
          <p className="ds-mini">Uso libre para reportajes, redes y publicaciones, citando &laquo;Bar Cabrero · Torrelavega&raquo;. Para campañas comerciales, escríbenos.</p>
          <ul className="ds-press-grid">
            {PRESS_IMAGES.map((img) => {
              const thumb = img.src.replace('/dishes/', '/dishes/thumbs/').replace(/\.jpg$/, '.webp');
              return (
                <li key={img.src}>
                  <a href={img.src} target="_blank" rel="noopener noreferrer" download data-testid={`dossier-img-${img.src.replace(/[^a-z]/gi, '-')}`}>
                    <img src={thumb} alt={img.label} loading="lazy" />
                    <span className="ds-press-label"><ImageIcon size={12} /> {img.label}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </section>

        {/* ── Contacto ── */}
        <section className="dossier-sec dossier-contact">
          <p className="ds-eyebrow">Contacto</p>
          <h3 className="ds-h">Cómo localizarnos</h3>
          <dl className="ds-contact-dl">
            <dt><Phone size={14} /> Teléfono</dt>
            <dd><a href={TEL_HREF}>{TEL_DISPLAY}</a></dd>

            <dt><MessageCircle size={14} /> WhatsApp prensa</dt>
            <dd><a href={WA_PRENSA} target="_blank" rel="noopener noreferrer">Chat directo</a></dd>

            <dt><Mail size={14} /> Email</dt>
            <dd><a href="mailto:info@barcabrero.com">info@barcabrero.com</a></dd>

            <dt><Instagram size={14} /> Instagram</dt>
            <dd><a href="https://instagram.com/cabrerobar" target="_blank" rel="noopener noreferrer">@cabrerobar</a></dd>

            <dt><MapPin size={14} /> Dirección</dt>
            <dd>C. Julián Ceballos 19 · 39300 Torrelavega · Cantabria</dd>

            <dt>Razón social</dt>
            <dd>Rumoroso56, S.L. · CIF B-39598716</dd>

            <dt>Web</dt>
            <dd><a href="https://barcabrero.com" target="_blank" rel="noopener noreferrer">barcabrero.com</a></dd>
          </dl>
        </section>

        <p className="dossier-foot no-print">
          Dossier actualizado: {new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })} ·
          ¿Necesitas algo más? Escríbenos por WhatsApp y respondemos en horario de bar.
        </p>
      </article>
    </>
  );
}
